import {
  STRIPE_PUBLIC_KEY,
  STRIPE_SEMIANNUAL_PRICE_ID,
  STRIPE_ANNUAL_PRICE_ID,
  STRIPE_SEMIANNUAL_PRODUCT_ID,
  STRIPE_ANNUAL_PRODUCT_ID,
  SUPABASE_FUNCTIONS_URL,
  SUPABASE_ANON_KEY,
} from "@/config/env";
import { loadStripe } from "@stripe/stripe-js";

// Types for Stripe products and prices
export interface StripeProduct {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  metadata?: Record<string, string>;
  // Additional fields from Stripe API
  default_price?: string;
  tax_code?: string;
  type?: string;
  created?: number;
  updated?: number;
  images?: string[];
  features?: string[];
  url?: string;
}

export interface StripePrice {
  id: string;
  productId: string;
  unitAmount: number;
  currency: string;
  recurring?: {
    interval: "day" | "week" | "month" | "year";
    intervalCount: number;
    trial_period_days?: number | null;
    usage_type?: string;
  };
  active: boolean;
  metadata?: Record<string, string>;
  // Additional fields from Stripe API
  created?: number;
  tax_behavior?: string;
  billing_scheme?: string;
  nickname?: string;
  lookup_key?: string;
}

// Default product information
const DEFAULT_PRODUCTS: Record<string, StripeProduct> = {
  [STRIPE_SEMIANNUAL_PRODUCT_ID]: {
    id: STRIPE_SEMIANNUAL_PRODUCT_ID,
    name: "Personal 6-Month Subscription",
    description: "6-month subscription for PetDocument",
    active: true,
  },
  [STRIPE_ANNUAL_PRODUCT_ID]: {
    id: STRIPE_ANNUAL_PRODUCT_ID,
    name: "Personal Annual Subscription",
    description: "Annual subscription for PetDocument with 2 months free",
    active: true,
  },
};

// Default price information
const DEFAULT_PRICES: Record<string, StripePrice> = {
  [STRIPE_SEMIANNUAL_PRICE_ID]: {
    id: STRIPE_SEMIANNUAL_PRICE_ID,
    productId: STRIPE_SEMIANNUAL_PRODUCT_ID,
    unitAmount: 2999, // $29.99
    currency: "usd",
    recurring: {
      interval: "month",
      intervalCount: 6,
    },
    active: true,
  },
  [STRIPE_ANNUAL_PRICE_ID]: {
    id: STRIPE_ANNUAL_PRICE_ID,
    productId: STRIPE_ANNUAL_PRODUCT_ID,
    unitAmount: 3999, // $39.99
    currency: "usd",
    recurring: {
      interval: "year",
      intervalCount: 1,
    },
    active: true,
  },
};

// Stripe instance
let stripePromise: Promise<any> | null = null;

// Cache for products and prices
let cachedProducts: Record<string, StripeProduct> = { ...DEFAULT_PRODUCTS };
let cachedPrices: Record<string, StripePrice> = { ...DEFAULT_PRICES };
let isInitialized = false;
let isLoading = false;
let hasAttemptedFetch = false;

/**
 * Get Stripe instance
 */
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLIC_KEY);
  }
  return stripePromise;
};

/**
 * Initialize the Stripe service by loading products and prices from Stripe API
 */
export const initializeStripeService = async (): Promise<void> => {
  if (isInitialized || isLoading) return;

  isLoading = true;

  try {
    // Load Stripe.js
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error("Failed to load Stripe.js");
    }

    // Fetch prices and products
    await fetchPriceData();

    isInitialized = true;
  } catch (error) {
    // Using default product and price data due to initialization failure
  } finally {
    isLoading = false;
  }
};

/**
 * Fetch price and product data from Supabase Edge Function
 */
const fetchPriceData = async (): Promise<void> => {
  if (hasAttemptedFetch) return;

  hasAttemptedFetch = true;

  try {
    // Fetch all data (prices and products) from the Edge Function
    const params = new URLSearchParams({
      priceIds: [STRIPE_SEMIANNUAL_PRICE_ID, STRIPE_ANNUAL_PRICE_ID].join(","),
      productIds: [STRIPE_SEMIANNUAL_PRODUCT_ID, STRIPE_ANNUAL_PRODUCT_ID].join(
        ","
      ),
    });

    const response = await fetch(
      `${SUPABASE_FUNCTIONS_URL}/get-stripe-data/all?${params}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
      }
    );

    if (response.ok) {
      const data = await response.json();

      // Process prices
      if (data.prices && Array.isArray(data.prices)) {
        data.prices.forEach((price: any) => {
          if (price.id) {
            cachedPrices[price.id] = {
              id: price.id,
              productId: price.product,
              unitAmount: price.unit_amount,
              currency: price.currency,
              recurring: price.recurring
                ? {
                    interval: price.recurring.interval,
                    intervalCount: price.recurring.interval_count,
                    trial_period_days: price.recurring.trial_period_days,
                    usage_type: price.recurring.usage_type,
                  }
                : undefined,
              active: price.active,
              metadata: price.metadata,
              // Additional fields
              created: price.created,
              tax_behavior: price.tax_behavior,
              billing_scheme: price.billing_scheme,
              nickname: price.nickname,
              lookup_key: price.lookup_key,
            };
          }
        });
      }

      // Process products
      if (data.products && Array.isArray(data.products)) {
        data.products.forEach((product: any) => {
          if (product.id) {
            cachedProducts[product.id] = {
              id: product.id,
              name: product.name,
              description: product.description,
              active: product.active,
              metadata: product.metadata,
              // Additional fields
              default_price: product.default_price,
              tax_code: product.tax_code,
              type: product.type,
              created: product.created,
              updated: product.updated,
              images: product.images,
              features: product.features,
              url: product.url,
            };
          }
        });
      }
    } else {
      const errorText = await response.text();
      console.error("Failed to fetch Stripe data:", errorText);
      console.warn("Using default price and product data");
    }
  } catch (error) {
    console.error("Failed to fetch Stripe data:", error);
    console.warn("Using default price and product data");
  }
};

/**
 * Get product information by product ID
 * This will attempt to initialize the service if it hasn't been initialized yet
 */
export const getProductById = (productId: string): StripeProduct | null => {
  // If not initialized, try to initialize
  if (!isInitialized && !isLoading) {
    // Stripe service not initialized, attempting to initialize now
    // Force initialization
    initializeStripeService().catch(console.error);
  }

  return cachedProducts[productId] || null;
};

/**
 * Get price information by price ID
 * This will attempt to initialize the service if it hasn't been initialized yet
 */
export const getPriceById = (priceId: string): StripePrice | null => {
  // If not initialized, try to initialize
  if (!isInitialized && !isLoading) {
    // Stripe service not initialized, attempting to initialize now
    // Force initialization
    initializeStripeService().catch(console.error);
  }

  return cachedPrices[priceId] || null;
};

/**
 * Check if the Stripe service is initialized
 */
export const isStripeServiceInitialized = (): boolean => {
  return isInitialized;
};

/**
 * Check if the Stripe service is loading
 */
export const isStripeServiceLoading = (): boolean => {
  return isLoading;
};

/**
 * Get formatted price amount (e.g., $9.99)
 */
export const formatPrice = (price: StripePrice): string => {
  const amount = price.unitAmount / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: price.currency,
  }).format(amount);
};

/**
 * Get billing interval description (e.g., "per month", "per year")
 */
export const getBillingInterval = (price: StripePrice): string => {
  if (!price.recurring) return "";

  const { interval, intervalCount } = price.recurring;

  if (intervalCount === 1) {
    return `per ${interval}`;
  }

  return `every ${intervalCount} ${interval}s`;
};

// Initialize the service when the module is imported
initializeStripeService().catch(console.error);

// For the fetchPrices function
export const fetchPrices = async (
  priceIds?: string[]
): Promise<Record<string, StripePrice>> => {
  try {
    // If no priceIds are provided, fetch the default ones
    if (!priceIds) {
      priceIds = [STRIPE_SEMIANNUAL_PRICE_ID, STRIPE_ANNUAL_PRICE_ID];
    }

    // ... rest of the function would go here
    return {}; // Return empty object for now
  } catch (error) {
    // ... error handling
    console.error("Error fetching prices:", error);
    return {}; // Return empty object on error
  }
};

// For the fetchProducts function
export const fetchProducts = async (
  productIds?: string[]
): Promise<Record<string, StripeProduct>> => {
  try {
    // If no productIds are provided, fetch the default ones
    if (!productIds) {
      productIds = [STRIPE_SEMIANNUAL_PRODUCT_ID, STRIPE_ANNUAL_PRODUCT_ID];
    }

    // ... rest of the function would go here
    return {}; // Return empty object for now
  } catch (error) {
    // ... error handling
    console.error("Error fetching products:", error);
    return {}; // Return empty object on error
  }
};
